import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import { ROUTE_SUPER_ADMIN } from "../../Util/VMConstants";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import { getAdmins, registerAdmin, formatApiDate, formatDisplayDateTime } from "../../api/superAdminApi";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const ADMIN_TYPE_OPTIONS = [
  { value: "SuperAdmin", label: "Super Admin (all cities)" },
  { value: "subAdmin", label: "Sub Admin (single city)" },
];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];

function adminLoginName(row) {
  return row.login_name ?? row.user_name ?? row.email ?? "—";
}

export default function VMSuperAdminAddAdmin() {
  const navigate = useNavigate();
  const { canManageAdmins } = useAdminPanel();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [adminType, setAdminType] = useState("subAdmin");
  const [status, setStatus] = useState("Active");
  const [submitting, setSubmitting] = useState(false);

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(true);

  const loc = useLocationCascading({ includeVenue: false });

  useEffect(() => {
    if (!canManageAdmins) {
      toast.info("Only Super Admin can manage admin users.");
      navigate(`/${ROUTE_SUPER_ADMIN}`, { replace: true });
    }
  }, [canManageAdmins, navigate]);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    const params = { page, no_of_rows: rowsPerPage };
    if (appliedSearch.trim()) params.search = appliedSearch.trim();
    const from = formatApiDate(fromDate);
    const to = formatApiDate(toDate);
    if (from) params.from = from;
    if (to) params.to = to;

    try {
      const res = await getAdmins(params);
      const body = res.data;
      setRows(Array.isArray(body?.data) ? body.data : []);
      setTotalRecords(body?.totalCount ?? 0);
    } catch {
      setRows([]);
      setTotalRecords(0);
      toast.info("Super admin list API unavailable.");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, appliedSearch, fromDate, toDate]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !loginEmail.trim() || !password) {
      toast.error("Name, login email and password are required.");
      return;
    }

    if (adminType === "subAdmin" && (!loc.countryLabel || !loc.stateLabel || !loc.cityLabel)) {
      toast.error("Country, state and city are required for subAdmin.");
      return;
    }

    setSubmitting(true);
    try {
      const loginName = loginEmail.trim();
      const res = await registerAdmin({
        name: name.trim(),
        login_name: loginName,
        email: loginName,
        password,
        mobile_no: mobileNo.trim() || undefined,
        admin_type: adminType,
        status,
        country: loc.countryLabel || (loc.countryId ? String(loc.countryId) : undefined),
        state: loc.stateLabel || (loc.stateId ? String(loc.stateId) : undefined),
        city: loc.cityLabel || (loc.cityId ? String(loc.cityId) : undefined),
      });
      if (res.data?.status_code === 200) {
        toast.success(res.data?.message || "Super admin created.");
        setName("");
        setLoginEmail("");
        setPassword("");
        setStatus("Active");
        loc.resetLocation();
        setShowForm(false);
        setPage(0);
        fetchAdmins();
      } else {
        toast.error(res.data?.message || "Unable to create super admin.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to create super admin.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleListSearch = () => {
    setAppliedSearch(searchInput.trim());
    setPage(0);
  };

  if (showForm) {
    return (
      <Box>
        <SuperAdminPageHeader
          title="Add User"
          actions={
            <Button variant="contained" size="small" onClick={() => setShowForm(false)}>
              Back
            </Button>
          }
        />

        <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3, maxWidth: 720, boxShadow: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="User Name" value={name} onChange={(e) => setName(e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Login Name" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Mobile" value={mobileNo} onChange={(e) => setMobileNo(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Admin Type</InputLabel>
                <Select label="Admin Type" value={adminType} onChange={(e) => setAdminType(e.target.value)}>
                  {ADMIN_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <SuperAdminLocationSelects {...loc} itemProps={{ xs: 12 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={submitting}>Add</Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <SuperAdminPageHeader
        title="Users"
        actions={<Button variant="contained" onClick={() => setShowForm(true)}>Add User</Button>}
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleListSearch()} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker label="From Date" value={fromDate} onChange={setFromDate} format="DD/MM/YYYY" slotProps={{ textField: { size: "small", fullWidth: true } }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker label="To Date" value={toDate} onChange={setToDate} format="DD/MM/YYYY" slotProps={{ textField: { size: "small", fullWidth: true } }} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button variant="contained" fullWidth onClick={handleListSearch} disabled={loading}>
              {loading ? <CircularProgress size={22} color="inherit" /> : "Search"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {["S.No", "Login Name", "Admin Type", "Scope", "Status", "Created At"].map((h) => (
                <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} aria-label="Loading administrators" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>No super admin users found.</TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={row.user_id ?? idx}>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{adminLoginName(row)}</TableCell>
                  <TableCell>{row.admin_type ?? "—"}</TableCell>
                  <TableCell>
                    {row.admin_scope?.city
                      ? [row.admin_scope.city, row.admin_scope.state].filter(Boolean).join(", ")
                      : "All"}
                  </TableCell>
                  <TableCell>{row.status ?? "—"}</TableCell>
                  <TableCell>{formatDisplayDateTime(row.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={totalRecords}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                colSpan={6}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
}
